"use client"

import type { ElementType } from "react"

import { CarouselDots, Reel, Text } from "@/components/ui"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

export interface BrandChip {
  id: string
  label: string
  image?: string
  imageCrop?: ImageCrop
  imageAlt?: string
  href?: string
  serveId?: string
}

const PAGE_SIZE = 8

const initials = (label: string) =>
  label
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()

const chunk = <T,>(items: T[], size: number) => Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, index * size + size))

const Chip = ({ item, className }: { item: BrandChip; className: string }) => {
  const Element: ElementType = item.href ? Link : "div"
  return (
    <Element
      {...(item.href ? { href: item.href } : {})}
      data-nelo-serve={item.serveId}
      className={`press group flex flex-col items-center focus-visible:outline-none ${className}`}
    >
      <div className="bg-surface-paper border-line @3xl:border-line-soft group-hover:ring-brand group-focus-visible:ring-brand duration-moderate ease-productive relative aspect-square w-full overflow-hidden rounded-full border ring-transparent ring-offset-2 transition-all group-hover:ring-1 group-focus-visible:ring-2">
        {item.image ? (
          <TileImage src={item.image} crop={item.imageCrop} alt={item.imageAlt ?? item.label} fill sizes="88px" className="object-contain" />
        ) : (
          <span aria-hidden className="text-fg-muted font-display text-body-lg absolute inset-0 grid place-items-center font-semibold">
            {initials(item.label)}
          </span>
        )}
      </div>
      <Text as="div" variant="body" family="text" truncate className="@3xl:text-body-lg w-full text-center">
        {item.label}
      </Text>
    </Element>
  )
}

export const BrandChips = ({ items }: { items: BrandChip[] }) => {
  const pages = chunk(items, PAGE_SIZE)
  const { ref, index, stops, overflowing, goTo } = useReelPagination(pages.length)
  return (
    <div>
      <Reel ref={ref} gap="500" snap inset className="-my-100 py-100 @3xl:hidden">
        {pages.map((page, pageIndex) => (
          <div key={pageIndex} className="grid w-full shrink-0 snap-start grid-cols-4 gap-x-500 gap-y-250">
            {page.map((item) => (
              <Chip key={item.id} item={item} className="gap-125" />
            ))}
          </div>
        ))}
      </Reel>
      {overflowing && (
        <div className="mt-500 flex justify-center @3xl:hidden">
          <CarouselDots count={stops} index={index} onChange={goTo} tone="plain" activeColor="bg-brand" />
        </div>
      )}
      <div className="hidden @3xl:block">
        <Reel gap="500" inset className="-my-100 py-100">
          {items.map((item) => (
            <Chip key={item.id} item={item} className="w-22 shrink-0 gap-250" />
          ))}
        </Reel>
      </div>
    </div>
  )
}
